import { useQuery } from '@apollo/client';
import {
  Icon,
  SelectionSheet,
  getTranslatedText,
  Loader
} from '@nile/components';
import { GET_ALL_TEMPLATELIST } from '@nile/graphql';
import { Button, Avatar, Sheet } from '@superops/ui-design-system';
import { FileImport } from '@styled-icons/fa-solid/FileImport';
import { Timer } from '@styled-icons/remix-fill';
import { pick } from 'lodash';
import { renderTime } from '@nile/projects/src/Utils';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { DisabledTag } from '@nile/mangifera/src/Pebbles/TicketContainer';
import { injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';

const TemplateHolder = styled.div`
  opacity: ${(props) => (props.disable ? '0.6' : '')};
  .Polaris-Avatar {
    min-width: 2rem;
    &.Polaris-Avatar--sizeMedium {
      width: 2rem;
      height: 2rem;
    }
  }
  .detail {
    font-size: 1.4rem !important;
    font-weight: 500;
    .timer {
      font-weight: bold;
    }
  }
`;

function CopyTaskContainer(props) {
  const {
    intl,
    templateList,
    usePropTemplates,
    hideSectionsInTask = []
  } = props;
  const { loading, data } = useQuery(GET_ALL_TEMPLATELIST, {
    fetchPolicy: 'network-only',
    skip: !!usePropTemplates
  });
  const context = props.context || {};
  const [selectedItems, setSelectedItems] = useState(
    context.value.taskTemplateList.map((el) => {
      return {
        ...el,
        id: el.taskTemplateId,
        name: el.title,
        label: el.title
      };
    })
  );

  if (loading)
    return (
      <div className="vw30 full-height">
        <Loader />
      </div>
    );
  let sections = [];
  const taskSection = {
    name: 'user',
    header: getTranslatedText(intl, 'admin.runbooks.tasks.alltasks'),
    items: usePropTemplates
      ? templateList
      : data.getTaskTemplateList.map((el) => {
          return {
            ...el,
            id: el.taskTemplateId,
            name: el.title,
            label: el.title
          };
        })
  };
  sections.push(taskSection);

  const updateParent = (selectedItem) => {
    props.updateTaskList(selectedItem);
  };

  const template = (item) => {
    return (
      <TemplateHolder disable={item.disable}>
        <div className="bold">{item.name}</div>
        <div className="flex mt10 center detail">
          {!hideSectionsInTask.includes('technician') && (
            <div className="flex mr25 center">
              <Avatar customer size="extraSmall" name={item.name} />
              {item?.technician?.name || item?.technician?.firstName ? (
                <div className="ml8">
                  {item.technician.name ||
                    `${item?.technician?.firstName || ''} ${item?.technician
                      ?.lastName || ''}`}
                </div>
              ) : (
                <DisabledTag className="ml8">NA</DisabledTag>
              )}
            </div>
          )}
          <div className="flex mr25 center timer">
            <SVGIcon name="EstimatedTime" />
            {renderTime(item?.estimatedTime) ? (
              <div className="ml8">{renderTime(item?.estimatedTime)}</div>
            ) : (
              <DisabledTag className="ml8">NA</DisabledTag>
            )}
          </div>
        </div>
      </TemplateHolder>
    );
  };

  return (
    <SelectionSheet
      sections={sections}
      actions={props.actions}
      meta={{
        heading:
          props.heading ||
          getTranslatedText(intl, 'admin.runbooks.tasks.header'),
        selectedTitle:
          props.selectedTitle ||
          getTranslatedText(intl, 'admin.runbooks.tasks.select')
      }}
      data={usePropTemplates ? templateList : data.getTaskTemplateList}
      selectedItems={selectedItems}
      toggleSheetActive={props.toggleSheetActive}
      updateParent={updateParent}
      template={template}
    />
  );
}

function CopyTask(props) {
  const { intl } = props;
  const [sheetActive, setSheetActive] = useState(false);
  const context = props.context || {};
  const toggleSheetActive = useCallback(
    () => setSheetActive((sheetActive) => !sheetActive),
    []
  );
  const updateTaskList = (items) => {
    const tasks = context.value.selectedTaskTemplates;
    let addedTasks = items.filter(
      (el) => !tasks.some((t) => t.taskTemplateId === el.taskTemplateId)
    );
    let removedTasks = tasks.filter(
      (el) => !items.some((i) => i.taskTemplateId === el.taskTemplateId)
    );
    addedTasks = addedTasks.map((el) => {
      const processedTechnician = el.technician
        ? pick(el.technician, ['firstName', 'lastName', 'userId'])
        : null;
      const processedTask = props.sendAll
        ? el
        : pick(el, [
            'taskTemplateId',
            'title',
            'description',
            'estimatedTime',
            'techGroup'
          ]);
      processedTask.technician = processedTechnician;
      return processedTask;
    });
    removedTasks = removedTasks.map((el) => {
      return props.sendAll
        ? el
        : pick(el, [
            'taskTemplateId',
            'title',
            'description',
            'estimatedTime',
            'technician',
            'techGroup'
          ]);
    });
    let newTasks = tasks
      .filter((el) => items.some((i) => i.taskTemplateId === el.taskTemplateId))
      .concat(addedTasks);
    addedTasks = addedTasks.map((t, i) => ({
      ...t,
      taskOrder: (tasks?.length ?? 0) + i + 1
    }));
    newTasks = newTasks.map((t, i) => ({ ...t, taskOrder: i + 1 }));
    if (context.updateProperties) {
      context.updateProperties({
        addTaskTemplateList: addedTasks,
        removeTaskTemplateList: removedTasks,
        taskTemplateList: newTasks
      });
    } else {
      context.updateContext('addTaskTemplateList', addedTasks);
      context.updateContext('removeTaskTemplateList', removedTasks);
      context.updateContext('taskTemplateList', newTasks);
    }
  };

  return (
    <span>
      <Button
        id="copy"
        secondary
        onClick={toggleSheetActive}
        plain={props?.isLink}
        plainSecondary={props?.isText}
        icon={props?.icon}
      >
        <span className="fx actr">
          {!props?.hideIcon && !props.icon && (
            <div className="mr-10 fx actr">
              <SVGIcon name="Import" />
            </div>
          )}
          {props?.name || getTranslatedText(intl, 'admin.runbooks.tasks.copy')}
        </span>
      </Button>
      <Sheet open={sheetActive} onClose={toggleSheetActive}>
        <CopyTaskContainer
          intl={intl}
          actions={props.actions}
          templateList={props.tasksList}
          toggleSheetActive={toggleSheetActive}
          updateTaskList={updateTaskList}
          context={context}
          usePropTemplates={props.usePropTemplates}
          hideSectionsInTask={props.hideSectionsInTask}
        ></CopyTaskContainer>
      </Sheet>
    </span>
  );
}

export default injectIntl(CopyTask);
